/**
 * PLAT-11213 DS2 — the name every reel is created with, and the test for it.
 *
 * Every creation path titles a reel `<Story title> - Compilation`:
 * `AIClipProcessor.getReelTitle` for the Reel Producer, `AIClipProducer` for the
 * Transcript-tab combine, `useSaveClipsAsReel` for Save as Reel. That makes the
 * title itself the record of "nobody has named this", which is what the
 * automatic naming run is guarded on — there is no stored `nameSource` for a
 * reel, because a rename from a clip card or the Creator Studio selector writes
 * the title without knowing this feature exists and a stored flag would go stale
 * against exactly those writes.
 *
 * A predicate over a string is only as good as the string being written in one
 * place, so the suffix lives here and the three creation paths build from it.
 *
 * Pure — no React, no MobX, no store access.
 */

/** What separates the story's title from the word that marks it a placeholder. */
const COMPILATION_SUFFIX = ' - Compilation';

/**
 * The placeholder for a reel cut from `storyTitle`.
 *
 * The callers resolve their own fallback for a missing story title — they have
 * different ones, and which stand-in a path uses is that path's business; the
 * suffix is what has to agree.
 */
export function reelPlaceholderTitle(storyTitle: string): string {
  return `${storyTitle}${COMPILATION_SUFFIX}`;
}

/**
 * Whether `title` is still the placeholder this story's reels are created with.
 *
 * An exact match, not a "ends in Compilation" test: a creator is entitled to
 * name a reel `Board Meeting - Compilation` and a reel carried over from another
 * story is not this story's placeholder. Both sides are trimmed so a story whose
 * own title has a trailing space still recognises its reels.
 */
export function isReelPlaceholderTitle(
  title: string | undefined,
  storyTitle: string | undefined,
): boolean {
  if (!title || !storyTitle) return false;
  return title.trim() === reelPlaceholderTitle(storyTitle.trim());
}
